import { ACCOUNT_SETTINGS_TAB_QUERY_PARAM } from '@coursera-web/personalization-settings';

import { TopSections } from 'bundles/account-settings/constants/SettingsConstants';
import type { TopSectionValueType } from 'bundles/account-settings/types/Settings';

/**
 * Query param carrying the selected tab, e.g. `/account-settings?tab=Personalization`.
 *
 * The tab lives in the query rather than the hash so that it reaches the server and SSR can render
 * the correct panel on the first paint. A fragment is never sent in the HTTP request, so the older
 * `#Personalization` form always renders the default tab server-side and visibly swaps after
 * hydration.
 */
export const TAB_QUERY_PARAM = ACCOUNT_SETTINGS_TAB_QUERY_PARAM;

export const DEFAULT_TOP_SECTION: TopSectionValueType = TopSections.ACCOUNT;

type TabLocation = {
  query?: Record<string, unknown> | null;
  hash?: string | null;
};

/** Case-insensitively resolve a raw URL value to a known top section. */
export const parseTopSection = (value?: string | null): TopSectionValueType | undefined => {
  if (!value) return undefined;

  return (Object.values(TopSections) as TopSectionValueType[]).find(
    (section) => section.toLowerCase() === value.toLowerCase()
  );
};

const getQueryTab = (location: TabLocation): string | undefined => {
  const raw = location.query?.[TAB_QUERY_PARAM];
  return typeof raw === 'string' ? raw : undefined;
};

const getHashTab = (location: TabLocation): string | undefined => {
  const hash = location.hash;
  return hash ? hash.replace(/^#/, '') : undefined;
};

/**
 * Resolve the selected tab from a router location. The query param wins over the legacy hash so a
 * rewritten URL is stable.
 *
 * `source` lets the caller tell a legacy hash deep link apart from a canonical one, so only the
 * former triggers a URL rewrite. Note that a hash we don't recognise (such as the `#sound-effects`
 * section deep link) resolves to `'default'`, not `'hash'` — those hashes belong to other features
 * and must survive untouched.
 */
export const resolveTopSection = (
  location: TabLocation
): { section: TopSectionValueType; source: 'query' | 'hash' | 'default' } => {
  const fromQuery = parseTopSection(getQueryTab(location));
  if (fromQuery) return { section: fromQuery, source: 'query' };

  const fromHash = parseTopSection(getHashTab(location));
  if (fromHash) return { section: fromHash, source: 'hash' };

  return { section: DEFAULT_TOP_SECTION, source: 'default' };
};

/** Build the query object for a tab selection, preserving every other param. */
export const withTabQuery = (
  query: Record<string, unknown> | null | undefined,
  section: TopSectionValueType
): Record<string, unknown> => ({ ...query, [TAB_QUERY_PARAM]: section });
